import { useMemo } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  PieChart, Pie, Cell,
} from 'recharts';
import { Globe, TrendingUp, Users, Building2, CircleAlert as AlertCircle } from 'lucide-react';
import TamBreakdownCard from './TamBreakdownCard';
import { APP_CHART, healthChartColour } from '../../lib/chartTheme';
import type { TerritoryAnalyticsSnapshot, TerritoryHealthComponents, Account } from '../../types';

interface MemberSnapshot extends TerritoryAnalyticsSnapshot {
  user_name: string;
  user_email: string;
}

interface Props {
  mySnapshot: TerritoryAnalyticsSnapshot | null;
  allSnapshots: MemberSnapshot[];
  allAccounts: Account[];
  orgAvgHealth: number;
  orgId: string;
  onViewMember?: (userId: string, name: string) => void;
}

const TIER_COLORS = [APP_CHART.brand, APP_CHART.focus, APP_CHART.accent, APP_CHART.muted];

export default function ExecutiveDashboard({ mySnapshot, allSnapshots, allAccounts, orgAvgHealth, onViewMember }: Props) {
  const orgTierData = useMemo(() => {
    let a = 0, b = 0, c = 0, u = 0;
    for (const s of allSnapshots) {
      a += s.accounts_tiered_a;
      b += s.accounts_tiered_b;
      c += s.accounts_tiered_c;
      u += s.accounts_untiered;
    }
    return [
      { name: 'Tier A', value: a, fill: TIER_COLORS[0] },
      { name: 'Tier B', value: b, fill: TIER_COLORS[1] },
      { name: 'Tier C', value: c, fill: TIER_COLORS[2] },
      { name: 'Untiered', value: u, fill: TIER_COLORS[3] },
    ];
  }, [allSnapshots]);

  const crossTeamIndustry = useMemo(() => {
    const map = new Map<string, number>();
    for (const s of allSnapshots) {
      if (!s.industry_breakdown) continue;
      for (const [ind, cnt] of Object.entries(s.industry_breakdown)) {
        map.set(ind, (map.get(ind) || 0) + cnt);
      }
    }
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name, count]) => ({ name: name.length > 20 ? name.slice(0, 20) + '...' : name, count }));
  }, [allSnapshots]);

  const lowActivityMembers = useMemo(() => {
    return allSnapshots
      .filter(s => s.total_actions_30d < 5 && s.accounts_owned > 0)
      .sort((a, b) => a.total_actions_30d - b.total_actions_30d)
      .slice(0, 5);
  }, [allSnapshots]);

  const orgTotals = useMemo(() => {
    return {
      members: allSnapshots.length,
      accounts: allSnapshots.reduce((s, snap) => s + snap.accounts_owned, 0),
      contacts: allSnapshots.reduce((s, snap) => s + snap.total_contacts, 0),
      actions: allSnapshots.reduce((s, snap) => s + snap.total_actions_30d, 0),
      tamMin: allSnapshots.reduce((s, snap) => s + (snap.total_tam_min || 0), 0),
      tamMax: allSnapshots.reduce((s, snap) => s + (snap.total_tam_max || 0), 0),
    };
  }, [allSnapshots]);

  const healthRadar = useMemo(() => {
    if (allSnapshots.length === 0) return [];
    const avg = (key: keyof TerritoryHealthComponents) => {
      let sum = 0, count = 0;
      for (const s of allSnapshots) {
        if (s.health_components?.[key] !== undefined) { sum += s.health_components[key]; count++; }
      }
      return count > 0 ? sum / count : 0;
    };
    return [
      { subject: 'Tier Balance', value: avg('tier_balance') },
      { subject: 'Enrichment', value: avg('enrichment') },
      { subject: 'UC Coverage', value: avg('use_case_coverage') },
      { subject: 'UC Quality', value: avg('use_case_quality') },
      { subject: 'Activity', value: avg('activity') },
      { subject: 'ICP Fit', value: avg('icp_alignment') },
    ];
  }, [allSnapshots]);

  const formatTam = (val: number) => {
    if (val >= 1e9) return `$${(val / 1e9).toFixed(1)}B`;
    if (val >= 1e6) return `$${(val / 1e6).toFixed(1)}M`;
    if (val >= 1e3) return `$${(val / 1e3).toFixed(0)}K`;
    return `$${val.toFixed(0)}`;
  };

  // Group leaders from snapshots
  const leaderRows = useMemo(() => {
    return allSnapshots
      .filter(s => s.role_at_snapshot === 'leader' || s.role_at_snapshot === 'admin')
      .sort((a, b) => b.territory_health_score - a.territory_health_score);
  }, [allSnapshots]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-app-surface rounded-lg border border-app-line p-5">
          <div className="w-9 h-9 bg-app-canvas rounded-lg flex items-center justify-center mb-3">
            <Users className="w-4 h-4 text-app-muted" />
          </div>
          <p className="text-2xl font-bold text-app-ink">{orgTotals.members}</p>
          <p className="text-xs text-app-muted">Total Members</p>
        </div>
        <div className="bg-app-surface rounded-lg border border-app-line p-5">
          <div className="w-9 h-9 bg-app-canvas rounded-lg flex items-center justify-center mb-3">
            <Building2 className="w-4 h-4 text-app-muted" />
          </div>
          <p className="text-2xl font-bold text-app-ink">{orgTotals.accounts}</p>
          <p className="text-xs text-app-muted">Total Accounts</p>
        </div>
        <div className="bg-app-surface rounded-lg border border-app-line p-5">
          <p className="text-2xl font-bold" style={{ color: healthChartColour(orgAvgHealth) }}>{orgAvgHealth.toFixed(0)}</p>
          <p className="text-xs text-app-muted">Org Health Score</p>
        </div>
        <div className="bg-app-surface rounded-lg border border-app-line p-5">
          <div className="w-9 h-9 bg-app-canvas rounded-lg flex items-center justify-center mb-3">
            <TrendingUp className="w-4 h-4 text-app-muted" />
          </div>
          <p className="text-2xl font-bold text-app-ink">{orgTotals.actions}</p>
          <p className="text-xs text-app-muted">Total Actions (30d)</p>
        </div>
        <div className="bg-app-surface rounded-lg border border-app-line p-5">
          <div className="w-9 h-9 bg-app-canvas rounded-lg flex items-center justify-center mb-3">
            <Globe className="w-4 h-4 text-app-muted" />
          </div>
          <p className="text-lg font-bold text-app-ink">{formatTam(orgTotals.tamMin)} - {formatTam(orgTotals.tamMax)}</p>
          <p className="text-xs text-app-muted">Aggregate TAM</p>
        </div>
      </div>

      <TamBreakdownCard accounts={allAccounts} title="Organisation TAM Breakdown" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-app-surface rounded-lg border border-app-line p-6">
          <h3 className="text-sm font-semibold text-app-ink mb-4">Organisation Health Radar</h3>
          <ResponsiveContainer width="100%" height={250}>
            <RadarChart data={healthRadar}>
              <PolarGrid stroke={APP_CHART.line} />
              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 10, fill: APP_CHART.muted }} />
              <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
              <Radar dataKey="value" stroke={APP_CHART.brand} fill={APP_CHART.brand} fillOpacity={0.15} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-app-surface rounded-lg border border-app-line p-6">
          <h3 className="text-sm font-semibold text-app-ink mb-4">Organisation Tier Distribution</h3>
          <div className="flex items-center gap-8">
            <ResponsiveContainer width={180} height={180}>
              <PieChart>
                <Pie data={orgTierData} dataKey="value" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={2}>
                  {orgTierData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="space-y-3">
              {orgTierData.map(t => (
                <div key={t.name} className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: t.fill }} />
                  <span className="text-xs text-app-muted">{t.name}: <span className="font-semibold">{t.value}</span></span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {leaderRows.length > 0 && (
        <div className="bg-app-surface rounded-lg border border-app-line overflow-hidden">
          <div className="px-6 py-4 border-b border-app-line">
            <h3 className="text-sm font-semibold text-app-ink">Leader Comparison</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-app-canvas/60 text-[10px] font-semibold uppercase tracking-wider text-app-muted">
                  <th className="px-4 py-2 text-left">Leader</th>
                  <th className="px-4 py-2 text-center">Health</th>
                  <th className="px-4 py-2 text-right">Accounts</th>
                  <th className="px-4 py-2 text-right">Avg Score</th>
                  <th className="px-4 py-2 text-right">Actions (30d)</th>
                  <th className="px-4 py-2 text-right">Enrichment</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-app-line">
                {leaderRows.map(lt => (
                  <tr key={lt.user_id} onClick={() => onViewMember?.(lt.user_id, lt.user_name)} className={`hover:bg-forest-50/50 transition-colors ${onViewMember ? 'cursor-pointer' : ''}`}>
                    <td className="px-4 py-2.5">
                      <p className="font-medium text-app-ink truncate max-w-[160px]">{lt.user_name}</p>
                      <p className="text-[10px] text-app-muted">{lt.user_email}</p>
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <span className="inline-flex items-center justify-center w-10 h-6 rounded-full text-xs font-bold text-white" style={{ backgroundColor: healthChartColour(lt.territory_health_score) }}>
                        {Math.round(lt.territory_health_score)}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right text-xs text-app-ink">{lt.accounts_owned}</td>
                    <td className="px-4 py-2.5 text-right text-xs font-mono text-app-ink">{lt.avg_tier_score?.toFixed(1)}</td>
                    <td className="px-4 py-2.5 text-right text-xs text-app-ink">{lt.total_actions_30d}</td>
                    <td className="px-4 py-2.5 text-right text-xs text-app-muted">{lt.enrichment_complete_pct?.toFixed(0)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {crossTeamIndustry.length > 0 && (
        <div className="bg-app-surface rounded-lg border border-app-line p-6">
          <h3 className="text-sm font-semibold text-app-ink mb-4">Cross-Team Industry Heatmap</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={crossTeamIndustry} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke={APP_CHART.line} />
              <XAxis type="number" tick={{ fontSize: 10, fill: APP_CHART.muted }} />
              <YAxis dataKey="name" type="category" width={130} tick={{ fontSize: 10, fill: APP_CHART.muted }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Bar dataKey="count" fill={APP_CHART.brand} radius={[0, 4, 4, 0]} barSize={16} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {lowActivityMembers.length > 0 && (
        <div className="bg-app-surface rounded-lg border border-app-warning overflow-hidden">
          <div className="px-6 py-4 border-b border-app-warning bg-app-warning/10 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-app-warning" />
            <h3 className="text-sm font-semibold text-app-warning">Low Activity Alerts</h3>
          </div>
          <div className="divide-y divide-app-line">
            {lowActivityMembers.map(s => (
              <div key={s.user_id} className="px-6 py-3 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-app-ink">{s.user_name}</p>
                  <p className="text-xs text-app-muted">{s.accounts_owned} accounts, {s.total_contacts} contacts</p>
                </div>
                <span className="text-xs font-medium px-2 py-1 rounded-full bg-app-warning/10 text-app-warning">
                  {s.total_actions_30d} actions / 30d
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-app-surface rounded-lg border border-app-line overflow-hidden">
        <div className="px-6 py-4 border-b border-app-line">
          <h3 className="text-sm font-semibold text-app-ink">Member Leaderboard</h3>
          <p className="text-xs text-app-muted mt-0.5">All members ranked by territory health</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-app-canvas/60 text-[10px] font-semibold uppercase tracking-wider text-app-muted">
                <th className="px-4 py-2 text-left w-10">#</th>
                <th className="px-4 py-2 text-left">Member</th>
                <th className="px-4 py-2 text-center">Health</th>
                <th className="px-4 py-2 text-right">Accounts</th>
                <th className="px-4 py-2 text-right">Avg Score</th>
                <th className="px-4 py-2 text-right">Contacts</th>
                <th className="px-4 py-2 text-right">Actions (30d)</th>
                <th className="px-4 py-2 text-right">Enrichment</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-app-line">
              {[...allSnapshots].sort((a, b) => b.territory_health_score - a.territory_health_score).map((s, i) => (
                <tr key={s.user_id} onClick={() => onViewMember?.(s.user_id, s.user_name)} className={`hover:bg-forest-50/50 transition-colors ${onViewMember ? 'cursor-pointer' : ''}`}>
                  <td className="px-4 py-2.5 text-xs text-app-muted font-mono">{i + 1}</td>
                  <td className="px-4 py-2.5">
                    <p className="font-medium text-app-ink truncate max-w-[160px]">{s.user_name}</p>
                    <p className="text-[10px] text-app-muted truncate">{s.user_email}</p>
                  </td>
                  <td className="px-4 py-2.5 text-center">
                    <span className="inline-flex items-center justify-center w-10 h-6 rounded-full text-xs font-bold text-white" style={{ backgroundColor: healthChartColour(s.territory_health_score) }}>
                      {Math.round(s.territory_health_score)}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right text-xs text-app-ink">{s.accounts_owned}</td>
                  <td className="px-4 py-2.5 text-right text-xs font-mono text-app-ink">{s.avg_tier_score?.toFixed(1) || '-'}</td>
                  <td className="px-4 py-2.5 text-right text-xs text-app-ink">{s.total_contacts}</td>
                  <td className="px-4 py-2.5 text-right text-xs text-app-ink">{s.total_actions_30d}</td>
                  <td className="px-4 py-2.5 text-right text-xs text-app-muted">{s.enrichment_complete_pct?.toFixed(0)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
